import assert from '@/sui/tools/assert'
import ctool from 'color'
import clone from 'clone'
import results from '@/sui/data/results'
import options from '@/sui/tools/chart/options'
import math from '@/sui/tools/math'

function wrapText (ctx, text, maxWidth) {
  assert(ctx != null)
  assert(text != null)
  assert(maxWidth != null)

  const words = text.split(' ')
  const lines = []
  let currentLine = words[0]

  for (let i = 1; i < words.length; ++i) {
    const word = words[i]
    const newLine = `${currentLine} ${word}`
    var width = ctx.measureText(newLine).width
    if (width < maxWidth) {
      currentLine = newLine
    } else {
      lines.push(currentLine)
      currentLine = word
    }
  }
  lines.push(currentLine)
  return lines
}

export function limitText (text) {
  assert(text != null)
  const maxLabelLength = 40
  return text.length > maxLabelLength
    ? `${text.substring(0, maxLabelLength - 3)}...` : text
}

export function freezeText (text) {
  assert(text != null)
  return text.replace(/ /gi, '@')
}

function unfreezeText (text) {
  assert(text != null)
  return text.replace(/@/gi, ' ')
}

function drawText (ctx, text, y, maxWidth, fontSize, fillStyle, bold) {
  assert(ctx != null)
  assert(text != null)
  assert(y != null)
  assert(maxWidth != null)
  assert(fontSize != null)

  ctx.font = `${bold ? 'bold ' : ''}${fontSize}px Avenir`
  ctx.fillStyle = fillStyle || '#2c3e50'
  if (fillStyle != null) {
    ctx.fillStyle = fillStyle
  }
  const texts = wrapText(ctx, text, maxWidth * 0.9)
  texts.forEach(
    i => {
      const t = unfreezeText(i)
      const m = ctx.measureText(t)
      let x = Math.round((maxWidth - m.width) / 2)
      ctx.fillText(t, x, y)
      y += fontSize + 2
    }
  )
  return y
}

export class Labels {
  constructor () {
    this._values = []
  }

  values () {
    return this._values
  }

  addLabel (label, value, stats) {
    assert(label != null)
    this._values.push(this.build(label, value, stats))
  }

  setLabel (index, label) {
    assert(index != null)
    assert(label != null)
    this._values[index].label = label
  }

  build (label, value, stats) {
    assert(label != null)

    const result = {
      label: `${label}`
    }

    if (value != null) {
      result.indicator = value
    }

    if (stats != null) {
      result.stats = stats
    }

    return result
  }
}

function fade (c, v) {
  assert(c != null)
  assert(v != null)
  return c.fade(v).hsl().string()
}

function incidenceBoxPlotFontSizes (count) {
  assert(count != null)

  if (count > 12) {
    return { fs: 20, lfs: 12 }
  }

  if (count > 10) {
    return { fs: 12, lfs: 8 }
  }

  if (count > 6) {
    return { fs: 14, lfs: 8 }
  }

  if (count > 3) {
    return { fs: 16, lfs: 10 }
  }

  return { fs: 20, lfs: 12 }
}

export function toChartJSBoxplot (stat, p) {
  assert(stat != null)
  assert(p != null)
  return {
    min: stat.min,
    max: stat.max,
    mean: stat.mean,
    median: stat.median,
    q1: stat.p25,
    q3: stat.p75,
    whiskerMin: stat.d1,
    whiskerMax: stat.d9,
    inf: stat.inf,
    sup: stat.sup,
    items: p
  }
}

export function repColors (v) {
  assert(v != null)
  let base = ctool('#45c7ef')
  const rotate = v.length < 6 ? 50 : 310 / v.length
  const getColor = v.length < 20 ? (i, b) => b.hex() : (i, b) => {
    const v = i % 5
    switch (v) {
      case 0 : return b.darken(0.15).hex()
      case 1 : return b.lighten(0.1).hex()
      case 2 : return b.darken(0.1).hex()
      case 3 : return b.lighten(0.15).hex()
    }

    return b.hex()
  }

  const result = []
  for (let i = 0; i !== v.length; ++i) {
    const r = v[i]
    const c = r.color != null ? r.color : getColor(i, base)
    result.push(c)

    base = base.rotate(rotate)
  }

  return result
}

function indicatorColor (v) {
  assert(v != null)
  if (v <= 50) {
    return '#888888'
  }

  if (v <= 80) {
    return '#F9434F'
  }

  if (v <= 90) {
    return '#F7A438'
  }

  return '#71CE3C'
}

export function classicBoxAndViolinPlot (title, points, stats, d) {
  const addDecimalScalePlugin =
      d.minimizeDecimalScale != null && d.minimizeDecimalScale

  function getLabel (decimal = 3) {
    const dOpt = results.getDecimalsOptions(decimal, d.minimizeDecimalScale)
    return `moyenne : ${results.fixDecimals(stats.mean, dOpt)},
médiane : ${results.fixDecimals(stats.median, dOpt)}, effectif : ${points.length}`
  }

  const addPluginList = []
  if (addDecimalScalePlugin) {
    addPluginList.push(options.plotDecimalScale())
  }

  const mme = getLabel()
  return {
    exportConfig: [
      options.title,
      options.titleFontSize(12),
      options.datalabelFontSize(),
      options.axesFontSize,
      options.maxYValue(`Valeur maximale`),
      options.minYValue(`Valeur minimale`),
      ...addPluginList
    ],
    config: {
      options: {
        title: {
          display: true,
          text: title
        },
        decimalScale: 3,
        tooltips: {
          titleFontSize: 0,
          titleMarginBottom: 0,
          backgroundColor: ctool('#444433b0').hsl().string(),
          callbacks: {
            label (item, data) {
              return `${mme}`
            }
          }
        },
        responsive: true,

        legend: {
          display: false
        },

        hover: {
          mode: null
        },
        elements: {
          boxandwhiskers: {
            itemRadius: 3,
            outlierRadius: 0
          },
          violin: {
            itemRadius: 3,
            outlierRadius: 0
          },
          rectangle: {
            borderRadius: 4
          }
        },
        plugins: {
          datalabels: {
            display: false
          }
        },
        scales: {
          xAxes: [{
            type: 'category',
            labels: [mme],
            ticks: {
              callback: function (value, idx) {
                return [getLabel(this.chart.options.decimalScale)]
              }
            }
          }]
        }
      },
      data: {
        datasets: [
          {
            barPercentage: 0.5,
            data: [points],
            backgroundColor: [
              ctool('#45c7ef').lighten(0.3).fade(0.7).hsl().string()
            ],
            borderColor: [ctool('#45c7ef').hsl().string()],
            borderWidth: [2]
          }
        ]
      }
    }
  }
}

export const incidenceBoxPlot = {
  resultTypes: [
    { v: 0, l: 'Indicateurs' },
    { v: 1, l: 'Médianes' },
    { v: 2, l: 'Moyennes' }
  ],

  add: function (dataset, labels, value, popData, color, title, allowEmpty, stats = null) {
    assert(dataset != null)
    assert(labels != null)
    assert(color != null)
    assert(title != null)
    assert(allowEmpty != null)

    if (value == null && !allowEmpty) {
      return
    }

    if (popData == null) {
      const v = value.incidence
      popData = {
        pop: [v],
        n: 1
      }
    }

    const points = popData.pop
    if (stats == null) {
      stats = math.statistics(points)
    }

    dataset.data.push(toChartJSBoxplot(stats, points))

    const nv = clone(value)
    if (value != null &&
            value.quality == null &&
            stats.quality != null) {
      nv.quality = stats.quality
    }

    if (labels) {
      labels.addLabel(title, nv, stats)
    }

    dataset.values.push({
      value: nv,
      median: stats.median
    })

    const op = dataset.showBoxPlot ? 0.3 : 1

    dataset.backgroundColor.push(fade(color, op))
    dataset.borderColor.push(fade(color.rotate(8).darken(0.3), op))
    dataset.medianColor.push(fade(color.rotate(8).darken(0.5), op))
  },

  plugins: {
    drawTitle: function (id) {
      return {
        id: id == null ? 'drawTitle' : id,
        beforeDraw: function (chart) {
          const width = chart.chart.width
          const ctx = chart.chart.ctx
          ctx.restore()
          ctx.textBaseline = 'middle'

          const tfs = chart.options.title.fontSize
          const plotTitles = chart.options.title.titles
          let y = drawText(
            ctx, plotTitles.main, 14, width,
            tfs == null ? 12 : tfs, null, true
          )

          const sfs = chart.options.title.subtitleFontSize
          y = drawText(
            ctx, plotTitles.sub, y, width,
            sfs == null ? 10 : sfs
          )

          ctx.save()
          if (plotTitles.warning != null) {
            drawText(
              ctx, plotTitles.warning, y + 10, width,
              sfs == null ? 10 : sfs, '#da7f09'
            )
            ctx.save()
            ctx.restore()
          }
        }
      }
    },

    indicatorLimit: function (limit) {
      if (limit == null) {
        return {}
      }

      return {
        id: 'indicatorLimit',
        afterDatasetDraw: function (chart, meta) {
          assert(chart != null)
          assert(meta != null)
          const dset = chart.data.datasets[meta.index]

          const data = dset.data
          if (data == null) {
            return null
          }

          const scaleY = chart.scales['y-axis-0']
          const scaleX = chart.scales['x-axis-0']
          const stepX = (scaleX.right - scaleX.left) / data.length
          const context = chart.ctx
          const c = '#F7404D'
          context.fillStyle = `${c}20`
          context.strokeStyle = `${c}f9`
          context.lineWidth = 3

          data.forEach(
            (d, i) => {
              if (d == null) {
                return
              }

              const limity = scaleY.getPixelForValue(limit)
              const left = scaleX.left + i * stepX

              if (limit >= 0 && limit < scaleY.max) {
                context.setLineDash([10, 5])
                context.beginPath()
                context.moveTo(left, limity)
                context.lineTo(left + stepX, limity)
                context.fillRect(left, scaleY.top, stepX, limity - scaleY.top)
                context.stroke()
              }
            }
          )
        }
      }
    },

    outlierLimit: function () {
      return {
        id: 'outlierLimit',
        afterDatasetDraw: function (chart, meta) {
          const dset = chart.data.datasets[meta.index]
          if (!dset.showOutliers) {
            return null
          }

          const data = dset.data
          if (data == null) {
            return null
          }

          const scaleY = chart.scales['y-axis-0']
          const scaleX = chart.scales['x-axis-0']
          const stepX = (scaleX.right - scaleX.left) / data.length
          const context = chart.ctx
          const c = results.qualities[2].color
          context.fillStyle = `${c}39`
          context.strokeStyle = `${c}a9`

          data.forEach(
            (d, i) => {
              if (d == null) {
                return
              }

              const limitSup = d.sup
              const limitySup = scaleY.getPixelForValue(limitSup)
              const left = scaleX.left + i * stepX

              if (limitSup >= 0 && limitSup < scaleY.max) {
                context.beginPath()
                context.moveTo(left, limitySup)
                context.lineTo(left + stepX, limitySup)
                context.fillRect(left, scaleY.top, stepX, limitySup - scaleY.top)
                context.stroke()
              }

              const limitInf = d.inf
              const limityInf = scaleY.getPixelForValue(limitInf)

              if (limitInf >= 0) {
                context.beginPath()
                context.moveTo(left, limityInf)
                context.lineTo(left + stepX, limityInf)
                context.fillRect(left, limityInf, stepX, scaleY.bottom - limityInf)
                context.stroke()
              }
            }
          )
        }
      }
    }
  },

  options: function (sc, titles, mainColor, labels, minimizeDecimalScale) {
    const count = labels.values().length
    const fonts = incidenceBoxPlotFontSizes(count)

    function getDecimalScaleOpt (scale) {
      return results.getDecimalsOptions(scale, minimizeDecimalScale)
    }

    const { showOutlierLimit, showIndicatorLimit, showValues } = sc

    return {
      labels: labels,
      decimalScale: 3,
      showQ1Q3: true,
      showSd: true,
      showIndicatorColors: true,

      tooltips: {
        titleFontSize: 0,
        titleMarginBottom: 0,
        backgroundColor: ctool('#444433b0').hsl().string(),
        borderColor: mainColor.darken(0.5).hex(),

        callbacks: {
          label (item, data) {
            assert(item != null)
            assert(data != null)

            if (item.datasetIndex !== 0) {
              return null
            }
            const d = data.datasets[item.datasetIndex]
            const dsLabel = d.label || ''
            const axesLabel = typeof item.xLabel === 'string'
              ? item.xLabel : item.yLabel
            const label = `${dsLabel} ${axesLabel}`
            const value = d.data[item.index]
            if (value == null) {
              return `${label}`
            }
            const eff = value.items.length
            return `${label} (moyenne : ${results.fixDecimals(value.mean)},
médiane : ${results.fixDecimals(value.median)}, effectif : ${eff})`
          }
        }
      },

      layout: {
        padding: sc.padding || {
          top: 110,
          left: 10,
          right: 30
        }
      },

      responsive: true,

      legend: { display: false },
      title: {
        display: false,
        titles: titles,
        fontSize: sc.titleFontSize || 16,
        subtitleFontSize: sc.subtitleFontSize || 14
      },

      hover: { mode: null },

      elements: {
        boxandwhiskers: sc.boxandwhiskers ? sc.boxandwhiskers : {
          itemRadius: 3,
          outlierRadius: 0
        },
        rectangle: {
          borderWidth: 2,
          borderRadius: 4
        }
      },

      scales: {
        xAxes: [
          {
            barPercentage: count === 1 ? 0.5 : 0.9,
            type: 'category',
            labels: labels.values(),
            ticks: {
              fontSize: fonts.lfs,

              callback: function (value, idx) {
                assert(value != null)
                const { label, stats, indicator } = value
                const result = [ label ]
                if (stats == null) {
                  return result
                }

                const { showSd, showQ1Q3, decimalScale } = this.chart.options
                const count = stats.n

                if (indicator != null && indicator.indicator != null) {
                  const ok = indicator.indicator.valid
                  result.push(`${ok} / ${count} dans la cible`)
                } else {
                  const outliers = stats.nOutTotal
                  result.push(
                    `effectif=${count}, ${outliers} ${outliers !== 1 ? 'outliers' : 'outlier'}`
                  )
                }

                const dOpt = getDecimalScaleOpt(decimalScale)
                const sd = stats.sd
                if (sd != null) {
                  const fixedMean = results.fixDecimals(stats.mean, dOpt)
                  const fixedMedian = results.fixDecimals(stats.median, dOpt)
                  let statLine = `moy=${fixedMean}  med=${fixedMedian}`

                  if (showSd) {
                    statLine += ` et=${results.fixDecimals(sd, dOpt)}`
                  }
                  result.push(statLine)

                  if (showQ1Q3) {
                    const fixedp25 = results.fixDecimals(stats.p25, dOpt)
                    const fixedp75 = results.fixDecimals(stats.p75, dOpt)
                    result.push(
                      `Q1=${fixedp25} Q3=${fixedp75}`
                    )
                  }
                }

                return result
              }
            }
          }
        ],

        yAxes: [
          {
            ticks: { min: 0 }
          }
        ]
      },

      plugins: {
        outlierLimit: showOutlierLimit,
        indicatorLimit: showIndicatorLimit,

        datalabels: {
          display: showValues,
          formatter: function (value, context) {
            assert(value != null)
            assert(context != null)

            const dset = context.dataset
            const values = dset.values
            if (values == null) {
              return null
            }

            const data = values[context.dataIndex]
            if (data == null) {
              return null
            }

            const dataValue = data.value
            if (dataValue == null) {
              return null
            }

            if (dset.showIndicator) {
              if (dataValue.indicator == null) {
                return null
              }

              return `${Number(dataValue.indicator.value).toFixed(2)}%`
            }

            const dOpt = getDecimalScaleOpt(
              context.chart.options.decimalScale
            )

            if (dset.showMean) {
              const mean = dataValue.mean
              if (mean == null) {
                return null
              }

              return results.fixDecimals(mean, dOpt)
            }

            if (dset.showMeanp) {
              const meanP = dataValue.meanp
              if (meanP == null) {
                return null
              }

              return results.fixDecimals(meanP, dOpt)
            }

            if (dset.showIncidence) {
              return results.fixDecimals(dataValue.incidence, dOpt)
            }
            return null
          },

          borderRadius: 3,

          padding: {
            top: 4,
            bottom: 2,
            right: 4,
            left: 4
          },

          font: {
            weight: 'bold',
            size: fonts.fs
          },

          color: 'white',
          anchor: 'center',
          align: 'top',

          backgroundColor: function (context) {
            assert(context != null)

            const dset = context.dataset
            const data = dset.values[context.dataIndex]
            if (data == null) {
              return null
            }

            const o = 0.9
            if (dset.showIndicator) {
              const indicator = data.value.indicator
              if (indicator == null) {
                return null
              }

              const showIndicatorColors = context.chart.options.showIndicatorColors
              const c = showIndicatorColors ? indicatorColor(indicator.value) : '#76cc78'
              return fade(ctool(c), 1 - o)
            }

            if (dset.showMean || dset.showMeanp) {
              return dset.borderColor
            }
            if (dset.showIncidence) { //
              const q = dset.showQuality ? data.value.quality : 0
              const op = sc.qualities[q].opacity * o
              const c = sc.qualities[q].color
              return fade(ctool(c), 1 - op)
            }
            return null
          },

          offset: function (context) {
            assert(context != null)
            const dset = context.dataset
            const data = dset.values[context.dataIndex]
            if (data == null) {
              return null
            }

            const scale = context.chart.scales['y-axis-0']
            const fontSize = context.chart.options.plugins.datalabels.font.size
            const padding = context.chart.options.plugins.datalabels.padding
            const centerOffset = 0.5 * (fontSize + padding.top + padding.bottom)
            if (dset.showMean || dset.showMeanp) {
              return -centerOffset
            }

            const mediany = scale.getPixelForValue(
              data.median
            )
            const above = mediany - scale.top - 10 + centerOffset
            if (dset.showIndicator) {
              return above
            }

            if (dset.showIncidence) {
              const over = data.value.quality === 3 ||
                                (data.value.incidence > scale.max)
              if (over) {
                return above
              }

              const valuey = scale.getPixelForValue(data.value.incidence)
              return mediany - valuey - centerOffset
            }
            return null
          }
        }
      }
    }
  }
}
